import { z } from 'zod'
import {
  SignatureSchema,
  SignaturePageSchema,
  SignaturePageTemplateSchema,
  SigningPositionSchema,
  UpdateSignatureCoordinatesSchema,
} from '../schemas'
import { http } from './http'
import { auth } from './auth'
import { parseWithZod } from '../utils/parse-with-zod'
import type { UpdateSignatureCoordinates } from '../types'

const SignatureResponseSchema = z.object({ signature: SignatureSchema })
const SignaturePageResponseSchema = z.object({ signature_page: SignaturePageSchema })
const SignaturePageTextResponseSchema = z.object({
  text: z.string(),
  status: z.enum(['generating', 'unavailable', 'ready']),
})
const SigningPositionResponseSchema = z.object({ signing_position: SigningPositionSchema })

export const signatures = {
  // ---- Signature pages ----------------------------------------

  getSignaturePage: async (id: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/signature_pages/${id}`, undefined, dealId)
    return parseWithZod(SignaturePageResponseSchema, result).signature_page
  },

  patchSignaturePage: async (id: number, attrs: { needs_review: boolean }, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.patch(`${regionHost}/api/signature_pages/${id}`, attrs, dealId)
    return parseWithZod(SignaturePageResponseSchema, result).signature_page
  },

  deleteSignaturePage: async (id: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    await http.delete(`${regionHost}/api/signature_pages/${id}`, dealId)
  },

  clearDetectedSignatures: async (versionId: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    await http.post(
      `${regionHost}/api/versions/${versionId}/clear_detected_signatures`,
      undefined,
      dealId,
    )
  },

  clearSignaturePageStatus: async (id: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/signature_pages/${id}/clear_signatures_status`,
      undefined,
      dealId,
    )
    return parseWithZod(z.object({ signature_pages: z.array(SignaturePageSchema) }), result)
      .signature_pages
  },

  getSignaturePageText: async (id: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/signature_pages/${id}/text`,
      undefined,
      dealId,
    )
    return parseWithZod(SignaturePageTextResponseSchema, result)
  },

  // ---- Signature page templates --------------------------------

  getSignaturePageTemplates: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/deals/${dealId}/signature_page_templates`,
      undefined,
      dealId,
    )
    return parseWithZod(
      z.object({ signature_page_templates: z.array(SignaturePageTemplateSchema) }),
      result,
    ).signature_page_templates
  },

  // ---- Signatures ---------------------------------------------

  deleteSignature: async (id: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    await http.delete(`${regionHost}/api/signatures/${id}`, dealId)
  },

  updateCoordinates: async (id: number, payload: UpdateSignatureCoordinates, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/signatures/${id}/update_coordinates`,
      parseWithZod(UpdateSignatureCoordinatesSchema, payload),
      dealId,
    )
    return parseWithZod(SignatureResponseSchema, result).signature
  },

  clearExecutedFile: async (id: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/signatures/${id}/clear_executed_file`,
      undefined,
      dealId,
    )
    return parseWithZod(SignatureResponseSchema, result).signature
  },

  // ---- Signing positions --------------------------------------

  createSigningPosition: async (
    dealId: number,
    payload: {
      ctype: 'officer' | 'personal'
      title?: string
      individual_id?: number
      entity_id?: number
    },
  ) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/deals/${dealId}/signing_positions`,
      payload,
      dealId,
    )
    return parseWithZod(SigningPositionResponseSchema, result).signing_position
  },

  patchSigningPosition: async (
    id: number,
    payload: { ctype?: 'officer' | 'personal'; title?: string },
    dealId: number,
  ) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.patch(`${regionHost}/api/signing_positions/${id}`, payload, dealId)
    return parseWithZod(SigningPositionResponseSchema, result).signing_position
  },

  deleteSigningPosition: async (id: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    await http.delete(`${regionHost}/api/signing_positions/${id}`, dealId)
  },
}
